import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { captureError } from '@/lib/logger';
import { driverProfileOf } from '@/lib/driverProfile';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
    User, Phone as PhoneIcon, Car, Shield, X, Check, Loader2,
    AlertTriangle, Trash2, Save,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DriverSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    driverId: number | null;
}

interface DriverProfile {
    /** NULL on the driver's own row — an admin may hold a test profile against the same id. */
    admin_id: number | null;
    id: number;
    first_name: string;
    middle_name: string | null;
    last_name: string;
    birth_date: string | null;
    phone: string | null;
    photo: string | null;
    address: string | null;
    email: string | null;
    user_id: string | null;
    created_at: string;
    updated_at: string | null;
}

interface DriverContract {
    id: number;
    created_at: string;
    sold_at: number | null;
    contract_updates: { id: number; status: string; created_at: string }[];
    vehicles: { id: number; make: string; model: string; color: string; vin: string } | null;
}

interface DriverData {
    id: number;
    created_at: string;
    updated_at: string;
    repayment_tag: string | null;
    profiles: DriverProfile | DriverProfile[];
    contracts: DriverContract[];
}

// ─── Steps ───────────────────────────────────────────────────────────────────

const STEPS = [
    { key: 'personal', label: 'Personal', icon: User, description: 'Name & identity', hasRequired: true },
    { key: 'contact', label: 'Contact', icon: PhoneIcon, description: 'Phone, email & address', hasRequired: true },
    { key: 'fleet', label: 'Fleet', icon: Car, description: 'Tags & contracts', hasRequired: false },
    { key: 'account', label: 'Account', icon: Shield, description: 'Auth & system info', hasRequired: false },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amount: number) {
    return new Intl.NumberFormat('en-GH', {
        style: 'currency', currency: 'GHS',
        minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(amount);
}

function getLatestStatus(contract: DriverContract) {
    const u = contract.contract_updates || [];
    if (u.length === 0) return null;
    return u.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]?.status || null;
}

function getStatusBadge(status: string | null) {
    switch (status) {
        case 'IN_PROGRESS': return <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>;
        case 'COMPLETED': return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Completed</Badge>;
        case 'TERMINATED': return <Badge className="bg-red-100 text-red-700 border-red-200">Terminated</Badge>;
        case 'DRAFT': return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Draft</Badge>;
        default: return <Badge variant="outline" className="text-muted-foreground">{status || 'Unknown'}</Badge>;
    }
}

// ─── Field component ─────────────────────────────────────────────────────────

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {label}{required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            {children}
        </div>
    );
}

// ─── Main Sheet ──────────────────────────────────────────────────────────────

export default function DriverSheet({ open, onOpenChange, driverId }: DriverSheetProps) {
    const queryClient = useQueryClient();
    const isCreate = driverId === null;
    const [step, setStep] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Form state
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [repaymentTag, setRepaymentTag] = useState('');

    // ── Fetch ──
    const { data: driver, isLoading } = useQuery({
        queryKey: ['driver-sheet', driverId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('drivers')
                .select(`
                    id, created_at, updated_at, repayment_tag,
                    profiles:profiles!profiles_driver_id_fkey(id, admin_id, first_name, middle_name, last_name, birth_date, phone, photo, address, email, user_id, created_at, updated_at),
                    contracts(id, created_at, sold_at, contract_updates(id, status, created_at), vehicles(id, make, model, color, vin))
                `)
                .eq('id', driverId!)
                .single();
            if (error) throw error;
            return data as unknown as DriverData;
        },
        enabled: open && !isCreate,
    });

    // The driver's own row, never an admin's test profile against the same driver id.
    const profile: DriverProfile | null = driverProfileOf(driver?.profiles);

    const fullName = profile
        ? [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(' ')
        : '';

    const contracts = useMemo(() =>
        (driver?.contracts || []).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
        [driver]
    );

    const hasContracts = contracts.length > 0;

    // ── Reset form ──
    useEffect(() => {
        if (open) {
            setStep(0);
            setDeleteDialogOpen(false);
            if (isCreate) {
                setFirstName(''); setMiddleName(''); setLastName('');
                setBirthDate(''); setPhone(''); setEmail('');
                setAddress(''); setRepaymentTag('');
            }
        }
    }, [open, isCreate]);

    // ── Populate form from fetched data ──
    useEffect(() => {
        if (!isCreate && profile && driver) {
            setFirstName(profile.first_name || '');
            setMiddleName(profile.middle_name || '');
            setLastName(profile.last_name || '');
            setBirthDate(profile.birth_date || '');
            setPhone(profile.phone || '');
            setEmail(profile.email || '');
            setAddress(profile.address || '');
            setRepaymentTag(driver.repayment_tag || '');
        }
    }, [isCreate, profile, driver]);

    const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: ['drivers'] });
        queryClient.invalidateQueries({ queryKey: ['drivers-count'] });
        queryClient.invalidateQueries({ queryKey: ['driver-sheet'] });
    };

    // ── Create ──
    const createMutation = useMutation({
        mutationFn: async () => {
            const { data: d, error: de } = await supabase
                .from('drivers')
                .insert({ repayment_tag: repaymentTag.trim() || null })
                .select('id')
                .single();
            if (de) throw de;

            const { error: pe } = await supabase
                .from('profiles')
                .insert({
                    user_id: null,
                    driver_id: d.id,
                    first_name: firstName.trim(),
                    middle_name: middleName.trim() || null,
                    last_name: lastName.trim(),
                    phone: phone.trim() || null,
                    address: address.trim() || null,
                    email: email.trim() || null,
                    birth_date: birthDate || null,
                });
            if (pe) {
                await supabase.from('drivers').delete().eq('id', d.id);
                throw pe;
            }
            return d;
        },
        onSuccess: () => {
            toast.success('Driver created');
            invalidateAll();
            onOpenChange(false);
        },
        onError: (e: any) => {
            captureError(e);
            if (e?.code === '23505' && e?.message?.includes('phone')) toast.error('Phone number already in use');
            else if (e?.code === '23505' && e?.message?.includes('email')) toast.error('Email already in use');
            else toast.error('Failed to create driver');
        },
    });

    // ── Update ──
    const updateMutation = useMutation({
        mutationFn: async () => {
            const { error: pe } = await supabase
                .from('profiles')
                .update({
                    first_name: firstName.trim(),
                    middle_name: middleName.trim() || null,
                    last_name: lastName.trim(),
                    phone: phone.trim() || null,
                    address: address.trim() || null,
                    email: email.trim() || null,
                    birth_date: birthDate || null,
                })
                // THE EXCLUSION IS NOT OPTIONAL ON A WRITE. `profiles.driver_id` is unique only
                // WHERE `admin_id IS NULL`, so `.eq('driver_id', …)` alone matches an admin's test
                // profile against the same driver too — and this UPDATE would overwrite that
                // admin's own name, phone, email and date of birth with the driver's. A client-side
                // pick cannot undo a row that has already been written.
                .eq('driver_id', driverId!)
                .is('admin_id', null);
            if (pe) throw pe;

            const { error: de } = await supabase
                .from('drivers')
                .update({ repayment_tag: repaymentTag.trim() || null })
                .eq('id', driverId!);
            if (de) throw de;
        },
        onSuccess: () => {
            toast.success('Driver updated');
            invalidateAll();
        },
        onError: (e: any) => {
            captureError(e);
            if (e?.code === '23505' && e?.message?.includes('phone')) toast.error('Phone number already in use');
            else if (e?.code === '23505' && e?.message?.includes('email')) toast.error('Email already in use');
            else toast.error('Failed to update driver');
        },
    });

    // ── Delete ──
    const deleteMutation = useMutation({
        mutationFn: async () => {
            const { error } = await (supabase as any).rpc('delete_driver', { p_driver_id: driverId });
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Driver deleted');
            invalidateAll();
            onOpenChange(false);
        },
        onError: (e: any) => {
            captureError(e);
            toast.error(e?.message || 'Failed to delete driver');
        },
    });

    // ── Step validation ──
    const isStepValid = (idx: number) => {
        if (idx === 0) return !!(firstName.trim() && lastName.trim());
        if (idx === 1) return !!phone.trim();
        return true;
    };

    const allValid = isStepValid(0) && isStepValid(1);

    const handleSave = () => {
        if (isCreate) createMutation.mutate();
        else updateMutation.mutate();
    };

    const handleClose = () => {
        onOpenChange(false);
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    const getInitials = (name: string) =>
        name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase().slice(0, 2) || '?';

    // ── Step content ──
    const renderStep = () => {
        switch (step) {
            case 0: // Personal
                return (
                    <div className="space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="First Name" required>
                                <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" />
                            </Field>
                            <Field label="Last Name" required>
                                <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" />
                            </Field>
                        </div>
                        <Field label="Middle Name">
                            <Input value={middleName} onChange={e => setMiddleName(e.target.value)} placeholder="Middle name" />
                        </Field>
                        <Field label="Date of Birth">
                            <DatePicker
                                date={birthDate ? new Date(birthDate) : undefined}
                                setDate={(date) => setBirthDate(date ? format(date, 'yyyy-MM-dd') : '')}
                            />
                        </Field>
                    </div>
                );

            case 1: // Contact
                return (
                    <div className="space-y-5">
                        <Field label="Phone Number" required>
                            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g. 233244000000" />
                        </Field>
                        <Field label="Email Address">
                            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="driver@example.com" />
                        </Field>
                        <Field label="Residential Address">
                            <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Plus code or street address" />
                        </Field>
                    </div>
                );

            case 2: // Fleet
                return (
                    <div className="space-y-5">
                        <Field label="Repayment Tag">
                            <Input value={repaymentTag} onChange={e => setRepaymentTag(e.target.value)} placeholder="Label for repayment tracking" />
                        </Field>

                        {!isCreate && (
                            <div>
                                <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-3 block">
                                    Contracts ({contracts.length})
                                </Label>
                                {contracts.length > 0 ? (
                                    <div className="space-y-2">
                                        {contracts.map(c => {
                                            const status = getLatestStatus(c);
                                            const v = c.vehicles;
                                            return (
                                                <div key={c.id} className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                        <Car className="h-4 w-4 text-muted-foreground shrink-0" />
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-sm truncate">
                                                                {v ? `${v.make} ${v.model}` : `Contract #${c.id}`}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {format(new Date(c.created_at), 'MMM d, yyyy')}
                                                                {c.sold_at ? ` · ${formatCurrency(c.sold_at)}` : ''}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {getStatusBadge(status)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground border rounded-lg bg-muted/20">
                                        <Car className="h-8 w-8 mx-auto mb-1.5 opacity-40" />
                                        <p className="text-xs">No contracts on record</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );

            case 3: // Account
                return (
                    <div className="space-y-5">
                        {!isCreate && profile ? (
                            <>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                                        <div>
                                            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Auth Account</p>
                                            <p className="font-medium text-sm mt-0.5">
                                                {profile.user_id ? (
                                                    <Badge className="bg-green-100 text-green-700 border-green-200">Linked</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">Not Linked</Badge>
                                                )}
                                            </p>
                                        </div>
                                        {profile.user_id && (
                                            <p className="text-[10px] text-muted-foreground font-mono">{profile.user_id.slice(0, 8)}…</p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="rounded-lg border p-3 bg-muted/30">
                                            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Driver ID</p>
                                            <p className="font-mono text-sm mt-0.5">{driver?.id}</p>
                                        </div>
                                        <div className="rounded-lg border p-3 bg-muted/30">
                                            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Driver Created</p>
                                            <p className="text-sm mt-0.5">{driver?.created_at ? format(new Date(driver.created_at), 'dd MMM yyyy') : '—'}</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="rounded-lg border p-3 bg-muted/30">
                                            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Profile Created</p>
                                            <p className="text-sm mt-0.5">{profile.created_at ? format(new Date(profile.created_at), 'dd MMM yyyy') : '—'}</p>
                                        </div>
                                        <div className="rounded-lg border p-3 bg-muted/30">
                                            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Last Updated</p>
                                            <p className="text-sm mt-0.5">{profile.updated_at ? format(new Date(profile.updated_at), 'dd MMM yyyy') : '—'}</p>
                                        </div>
                                    </div>
                                </div>

                            </>
                        ) : isCreate ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <Shield className="h-10 w-10 mx-auto mb-2 opacity-40" />
                                <p className="text-sm font-medium">Account details</p>
                                <p className="text-xs mt-1">Auth info will be generated after the driver is created.</p>
                            </div>
                        ) : null}
                    </div>
                );
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !p-0 !w-[85vw] !max-w-4xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
                >
                    {/* ── Left Sidebar ── */}
                    <div className="w-1/3 bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

                        <div className="mb-6 relative z-10">
                            {!isCreate && profile ? (
                                <>
                                    <h2 className="text-2xl font-semibold tracking-tight mb-4">Manage Driver</h2>
                                    <div className="flex items-center gap-3">
                                        <Avatar className="h-11 w-11">
                                            <SignedAvatarImage src={profile.photo || undefined} />
                                            <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                                                {getInitials(fullName)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0">
                                            <h3 className="text-lg font-semibold tracking-tight truncate">{fullName}</h3>
                                            <p className="text-xs text-muted-foreground">
                                                Driver since {driver?.created_at ? format(new Date(driver.created_at), 'MMM yyyy') : '…'}
                                            </p>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2 className="text-2xl font-semibold tracking-tight">New Driver</h2>
                                    <p className="text-sm text-muted-foreground mt-1">Add a new driver to the system.</p>
                                </>
                            )}
                        </div>

                        {/* Step nav */}
                        <div className="space-y-1 relative z-10 flex-1">
                            {STEPS.map((s, i) => {
                                const Icon = s.icon;
                                const isActive = i === step;
                                const isComplete = s.hasRequired && isStepValid(i);

                                return (
                                    <button
                                        key={s.key}
                                        onClick={() => setStep(i)}
                                        className={cn(
                                            "w-full group flex items-center gap-3 p-3 rounded-xl transition-all duration-300 text-left",
                                            isActive ? "bg-background shadow-sm ring-1 ring-border" : "hover:bg-background/50",
                                        )}
                                    >
                                        <div className="relative">
                                            <div className={cn(
                                                "h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300",
                                                isActive ? "bg-primary text-primary-foreground" :
                                                    isComplete ? "bg-green-100 text-green-600/70 dark:bg-green-900/40 dark:text-green-400/70" :
                                                        "bg-muted text-muted-foreground",
                                            )}>
                                                <Icon className={cn("h-5 w-5", isComplete && !isActive && "opacity-60")} />
                                            </div>
                                            {isComplete && !isActive && (
                                                <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-green-500 flex items-center justify-center ring-2 ring-background">
                                                    <Check className="h-2.5 w-2.5 text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <p className={cn(
                                                "font-medium text-sm transition-colors",
                                                isActive ? "text-foreground" : "text-muted-foreground"
                                            )}>
                                                {s.label}
                                            </p>
                                            <p className="text-xs text-muted-foreground/70 hidden lg:block">{s.description}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-auto flex items-center gap-6 relative z-10">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent"
                                onClick={handleClose}
                            >
                                <X className="h-4 w-4 mr-2" />
                                Close
                            </Button>
                            {!isCreate && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-fit text-destructive/70 hover:text-destructive p-0 h-auto hover:bg-transparent"
                                    onClick={() => setDeleteDialogOpen(true)}
                                    disabled={hasContracts}
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* ── Right Content ── */}
                    <div className="flex-1 flex flex-col h-full relative">
                        {/* Mobile header */}
                        <div className="md:hidden p-4 border-b flex items-center justify-between">
                            <div>
                                <h2 className="font-semibold">{STEPS[step].label}</h2>
                                <p className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={handleClose}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 flex flex-col overflow-hidden p-6 md:p-10">
                            <div className="flex flex-col h-full max-w-2xl mx-auto w-full">
                                {/* Step title */}
                                <div className="flex-none mb-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-2xl font-bold tracking-tight">{STEPS[step].label}</h3>
                                            <p className="text-muted-foreground">{STEPS[step].description}</p>
                                        </div>
                                        {STEPS[step].hasRequired && isStepValid(step) && (
                                            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium dark:bg-green-900/30 dark:text-green-400">
                                                <Check className="h-3.5 w-3.5" />
                                                Complete
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Scrollable form */}
                                <ScrollArea className="flex-1 min-h-0">
                                    {isLoading && !isCreate ? (
                                        <div className="space-y-4">
                                            <Skeleton className="h-10 w-full" />
                                            <Skeleton className="h-10 w-full" />
                                            <Skeleton className="h-10 w-full" />
                                        </div>
                                    ) : (
                                        renderStep()
                                    )}
                                </ScrollArea>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
                            <div className="max-w-2xl mx-auto flex items-center justify-between">
                                <Button
                                    variant="outline"
                                    onClick={handleClose}
                                    disabled={isSaving}
                                    className="rounded-full"
                                >
                                    Cancel
                                </Button>

                                <Button
                                    onClick={handleSave}
                                    disabled={!allValid || isSaving}
                                    className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                                >
                                    {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                    {isCreate ? 'Create Driver' : 'Save Changes'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Delete confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                            Delete Driver
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete <span className="font-medium text-foreground">{fullName}</span>,
                            their profile, and their authentication account (if linked).
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => deleteMutation.mutate()}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Delete Driver
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
